import { Link, Typography } from '@evaneos/b2b-ui';
import LuggageIcon from '@evaneos/b2b-ui/icons/LuggageOutlined';

import { useIntl } from 'react-intl';

import { DefinitionList } from '../../../design-system/DefinitionList/DefinitionList';
import { TripProject } from '../../../resources/dossier/types';
import { useDossierLayoutInfo } from '../DossierLayoutInfoContext';

type RequestContextProps = {
    tripProject: TripProject;
};

/**
 * Displays the context/origin of the initial request.
 *
 * - If an itinerary URL is present, shows a link to the itinerary (with its title, or a fallback label).
 * - Otherwise, falls back to the quoteType value (e.g. "Agency", "Destination").
 * - Renders nothing if neither itineraryUrl nor quoteType are available.
 */
export function RequestContext({ tripProject }: RequestContextProps) {
    const { formatMessage } = useIntl();
    const itineraryUrl =
        typeof tripProject.itineraryUrl?.value === 'string' ? tripProject.itineraryUrl.value : null;
    const itineraryTitle = tripProject.itineraryTitle?.valueTranslated;
    const quoteType = tripProject.quoteType?.valueTranslated;
    const { isFirstColumnNarrow } = useDossierLayoutInfo();

    if (!itineraryUrl && !quoteType) {
        return null;
    }

    return (
        <DefinitionList.Item>
            <DefinitionList.Label
                id="request-context"
                icon={<LuggageIcon fontSize="small" aria-hidden="true" />}
                iconOnly={isFirstColumnNarrow}
            >
                {formatMessage({ id: 'dossier.information.initial_request.context_title' })}
            </DefinitionList.Label>
            <DefinitionList.StaticValue>
                {itineraryUrl ? (
                    <Typography variant="body2">
                        {formatMessage({
                            id: 'dossier.information.initial_request.from_itinerary',
                        })}{' '}
                        <Link
                            href={itineraryUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="dossier-initial-request-itinerary_url"
                            fontWeight="bold !important"
                        >
                            {itineraryTitle ||
                                formatMessage({
                                    id: 'dossier.information.initial_request.itinerary.url',
                                })}
                        </Link>
                    </Typography>
                ) : (
                    <Typography variant="body2">{quoteType}</Typography>
                )}
            </DefinitionList.StaticValue>
        </DefinitionList.Item>
    );
}
